import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineWhatsappIcon from 'LuxKit/Icons/line/LineWhatsappIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import OfficeLocationModal, { OfficeLocations, useOfficeLocationContent } from 'components/Pages/AboutUsPage/OfficeLocationModal/OfficeLocationModal'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { NO_24_7_SUPPORT_REGIONS, PARTNER_BRANDS_ENABLED_REGIONS } from 'constants/config/region'
import ModalContext from 'contexts/ModalContext'
import GeoContext from 'contexts/geoContext'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'
import { rem } from 'polished'
import { useCallback, useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { useIntl, FormattedMessage } from 'react-intl'
import { staticPageMessages } from 'constants/config/i18nMessages'
import Accreditations from './Accreditations'
import FooterItemList from './FooterItemList'
import FooterListItem from './FooterListItem'
import FooterListItemsGrid from './FooterListItemsGrid'
import FooterList from './FooterList'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import { useAppSelector } from 'hooks/reduxHooks'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { useAppFooterObserver } from 'hooks/useAppLayoutObservers'
import { STORE_CAMPAIGN_URL } from 'constants/retailStores'

const Container = styled(Pane)`
  padding-block: ${rem(40)};
`

const StoreLocation = styled(BodyText)`
  padding-inline: ${rem(8)};
`

interface Props {
  isStoreMode: boolean
  region: string
}

function BottomSection(props: Props) {
  const { isStoreMode } = props
  const intl = useIntl()

  const currentYear = new Date().getFullYear()
  const { currentRegionCode } = useContext(GeoContext)
  const { offices } = useOfficeLocationContent()
  const showOtherPartners = !isStoreMode && PARTNER_BRANDS_ENABLED_REGIONS.includes(currentRegionCode)

  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)

  const showModal = useContext(ModalContext)
  const toggleOfficeModal = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const selectedOfficeLocation = e.currentTarget.dataset.key as OfficeLocations
    showModal(<OfficeLocationModal selectedOffice={selectedOfficeLocation} />)
  }, [showModal])

  // Separate retail stores and offices
  const retailStores = offices.filter(
    office => office.location === OfficeLocations.BONDI || office.location === OfficeLocations.CHADSTONE,
  )
  const officeLocations = offices.filter(
    office => office.location !== OfficeLocations.BONDI && office.location !== OfficeLocations.CHADSTONE,
  )

  const showAtolProtection = currentRegionCode === 'GB'

  const supportAssistantEnabled = useAppSelector(isSupportAssistantEnabled)
  const shouldShowSupportSection = supportAssistantEnabled || shouldRenderWhatsApp || shouldRenderTimely(currentRegionCode)

  const containerRef = useRef<HTMLDivElement>(null)
  useAppFooterObserver(containerRef)

  const getPageText = (pageType: string): string => {
    const pageTextMap: Record<string, string> = {
      aboutUs: intl.formatMessage({
        id: 'Ue3nau2E0FC1FhSy1hdZ',
        defaultMessage: 'About Us',
        description: 'About Us page link text in footer in the site footer',
      }),
      bestPrice: intl.formatMessage({
        id: 'iBFHA89WO2dvI5xPdHx-',
        defaultMessage: 'Best Price Guarantee',
        description: 'Best Price Guarantee page link text in footer in the site footer',
      }),
      careersAndCulture: intl.formatMessage({
        id: 'IhvoJIJrW8G8zhRd2Y2s',
        defaultMessage: 'Careers & Culture',
        description: 'Careers & Culture page link text in footer in the site footer',
      }),
      affiliatePage: intl.formatMessage({
        id: 'lWQYQHHkLjBAxjWKlONL',
        defaultMessage: 'Become an Affiliate Partner',
        description: 'Become an Affiliate Partner page link text in footer',
      }),
      termsAndConditions: intl.formatMessage({
        id: 'O4mvxtV38nn8mD9_mmex',
        defaultMessage: 'Terms & Conditions',
        description: 'Terms & Conditions page link text in footer in the site footer',
      }),
      privacyPolicy: intl.formatMessage(staticPageMessages.privacyPolicy),
      giftcardTermsAndConditions: intl.formatMessage({
        id: 'HyOsIbKuKrtcUx3wHnvV',
        defaultMessage: 'Luxury Escapes Gift Cards',
        description: 'Gift Cards page link text in footer on the gift card purchase or redemption page',
      }),
      bookingPolicies: intl.formatMessage(staticPageMessages.bookingPolicies),
      support: intl.formatMessage({
        id: 'VfljCcDT8ck6drXfYBG7',
        defaultMessage: 'Support',
        description: 'Support page link text in footer in the customer support section',
      }),
    }
    return pageTextMap[pageType] || ''
  }

  const luxuryEscapesItems = getStaticPagesByType(['aboutUs', 'bestPrice', 'careersAndCulture', 'affiliatePage'])
    .filter(page => !!page.url)
    .map(page => ({ ...page, text: getPageText(page.type) }))

  const helpItems = getStaticPagesByType(['termsAndConditions', 'privacyPolicy', 'giftcardTermsAndConditions', 'bookingPolicies', 'support'])
    .filter(page => !!page.url)
    .map(page => ({ ...page, text: getPageText(page.type) }))

  const otherPartners = getStaticPagesByType(['scoopon', 'cudo', 'deals', 'treatme', 'catch', 'travelshoot']).filter(
    page => !!page.url,
  )

  const customerSupportTitle = useMemo(
    () => {
      if (NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)) {
        return intl.formatMessage({
          id: 'znrMsbrvfjZkm5xVxqUr',
          defaultMessage: 'Customer support',
          description: 'Title for customer support section in footer in the support area',
        })
      }
      return intl.formatMessage({
        id: 'MF1oXvfB5cESneo1-Xj7',
        defaultMessage: '24-7 Customer support',
        description: 'Heading title for 24-7 customer support section in footer',
      })
    },
    [intl, currentRegionCode],
  )

  const chatNowTitle = intl.formatMessage({
    id: 'FpZIM0M99xZJ6k7EhPhF',
    defaultMessage: 'Chat now',
    description: 'Link text to start chat support in the customer support section',
  })

  const available247Subtitle = intl.formatMessage({
    id: 'DcKjsn5ptRaDEFIgUTMj',
    defaultMessage: 'Available 24/7',
    description: 'Subtitle indicating 24/7 availability for chat support',
  })

  const whatsappAriaLabel = intl.formatMessage({
    id: 'THZ2hkgAEmMN-QgD9it6',
    defaultMessage: 'WhatsApp',
    description: 'Aria label for the WhatsApp contact link in the site footer for screen readers',
  })
  const whatsappTitle = whatsappAriaLabel

  const bookStoreAppointmentTitle = intl.formatMessage({
    id: 'VOVDjMw4ETanzWjTEZ6T',
    defaultMessage: 'Book store appointment',
    description: 'Hyperlink text to book store appointment in the site footer',
  })

  const timelyBookingAriaLabel = intl.formatMessage({
    id: '1uLm_skCwWv5liCVGlRw',
    defaultMessage: 'Timely booking',
    description: 'Aria label for Timely booking link in the booking or order summary view',
  })

  const brandWebsiteTitle = config.footer.partners[0]?.href
    ? config.footer.partners[0].href.replace(/^https?:\/\/(www\.)?/, '').replace(/\/.*$/, '')
    : config.brandConfig.name

  const helpTitle = intl.formatMessage({
    id: 'FI110a_NmCwnQCQscLbN',
    defaultMessage: 'Help',
    description: 'Heading title for help links section in footer in the site footer',
  })

  const ourPartnerBrandsTitle = intl.formatMessage({
    id: 'UWcx7PzJrg_syzHBIIcj',
    defaultMessage: 'Our Partner Brands',
    description: 'Heading title for partner brands section in footer',
  })

  const luxuryEscapesLocationsTitle = intl.formatMessage({
    id: '-876cvdFdOdZWVtpThqb',
    defaultMessage: 'Luxury Escapes Locations',
    description: 'Title for Luxury Escapes locations section in footer',
  })

  const ourRetailStoresTitle = intl.formatMessage({
    id: '7-GMqMvjf8OATCzZItvZ',
    defaultMessage: 'Our Retail Stores',
    description: 'Heading title for retail stores section in footer in the site footer',
  })

  const ourOfficesTitle = intl.formatMessage({
    id: 'wl04nSvIHAtu4OlLA8J4',
    defaultMessage: 'Our Offices',
    description: 'Heading title for the offices section in footer in the site footer',
  })

  const luxuryEscapesStoreTitle = intl.formatMessage({
    id: 'rY2PvhkcP7HjaCZp4_8B',
    defaultMessage: 'Luxury Escapes Store',
    description: 'Heading title for Luxury Escapes store section in footer',
  })

  const atolProtectionTitle = intl.formatMessage({
    id: 'NUAdF4tNiX0SqsHekSqJ',
    defaultMessage: 'ATOL Protection',
    description: 'Title for ATOL protection section in footer in the insurance flow',
  })

  const copyrightText = useMemo(
    () =>
      intl.formatMessage(
        {
          id: 'rf42ulTmcMnf_Inj_4Qt',
          defaultMessage: 'Copyright © 2017 - {year} Luxury Escapes.',
          description: 'Copyright notice in footer with year in the site footer',
        },
        {
          year: currentYear,
        },
      ),
    [intl, currentYear],
  )

  const allRightsReservedText = intl.formatMessage({
    id: 'U-wHiyxI7rGvTqWGq9Rz',
    defaultMessage: 'All rights reserved',
    description: 'All rights reserved text in footer in the site footer',
  })

  return <Container background="light-background" className="hidden-print" ref={containerRef}>
    <LayoutContainer variant="large">
      <VerticalSpacer gap={56}>
        <FooterListItemsGrid>
          {shouldShowSupportSection && <FooterList title={customerSupportTitle}>
            {supportAssistantEnabled && <FooterListItem
              to="/support"
              rel="nofollow"
              title={chatNowTitle}
              subtitle={available247Subtitle}
              startIcon={<LineCommentsIcon />}
            />}
            {shouldRenderWhatsApp && <FooterListItem
              title={whatsappTitle}
              startIcon={<LineWhatsappIcon />}
              href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
              aria-label={whatsappAriaLabel}
              rel="noopener"
              target="_blank"
            />}
            {shouldRenderTimely(currentRegionCode) && <FooterListItem
              title={bookStoreAppointmentTitle}
              startIcon={<LuxuryEscapesSmallLogo width={24} height={24} aria-hidden />}
              href={getTimelyLink()}
              aria-label={timelyBookingAriaLabel}
              rel="noopener"
              target="_blank"
            />}
          </FooterList>}
          <FooterItemList title={brandWebsiteTitle} items={luxuryEscapesItems} />
          <FooterItemList title={helpTitle} items={helpItems} />
          {showOtherPartners && <FooterItemList title={ourPartnerBrandsTitle} items={otherPartners} />}
          {isStoreMode && <FooterItemList title={luxuryEscapesLocationsTitle} items={helpItems} />}
          {!isStoreMode && <Group direction="vertical" gap={24}>
            {shouldRenderTimely(currentRegionCode) && <FooterList title={ourRetailStoresTitle}>
              {retailStores.map((store) => (
                <FooterListItem
                  key={store.location}
                  title={store.displayLocation}
                  to={STORE_CAMPAIGN_URL}
                />
              ))}
            </FooterList>}
            <FooterList title={ourOfficesTitle}>
              {officeLocations.map((office) => <FooterListItem
                key={office.location}
                title={office.displayLocation}
                data-key={office.location}
                onClick={toggleOfficeModal}
              />)}
            </FooterList>
          </Group>}
          {isStoreMode && <FooterList title={luxuryEscapesStoreTitle}>
            <StoreLocation variant="medium">
              <FormattedMessage
                id="P7scfD178vcK2klo3pzl"
                defaultMessage="Shop B-186, Chadstone Shopping Centre{br}1341 Dandenong Road,{br}Chadstone VIC 3148"
                description="Full store address in footer - shop number, street and suburb"
                values={{
                  br: <br />,
                }}
              />
              {currentRegionCode === 'AU' && <><br /><br />{intl.formatMessage({
                id: 'yTkPG_nRFUuIohAirGUC',
                defaultMessage: 'Australian owned and operated.',
                description: 'Text indicating the company is Australian owned, shown for AU region in footer',
              })}
              </>}
            </StoreLocation>
          </FooterList>}
        </FooterListItemsGrid>
        {showAtolProtection && <VerticalSpacer gap={12}>
          <BodyText variant="large" weight="bold">{atolProtectionTitle}</BodyText>
          <BodyText variant="medium" align="justify">
            <FormattedMessage
              id="oEk6HtbWwYO7K1lKDJfa"
              defaultMessage="Some of the flights and flight-inclusive holidays on this website are financially protected by the ATOL scheme. But ATOL protection does not apply to all holiday and travel services listed on this website. This website will provide you with information on the protection that applies in the case of each holiday and travel service offered before you make your booking. If you do not receive an ATOL Certificate then the booking will not be ATOL protected. If you do receive an ATOL Certificate but all the parts of your trip are not listed on it, those parts will not be ATOL protected. Please see our booking conditions for information, or for more information about financial protection and the ATOL Certificate go to: <link>www.caa.co.uk</link>"
              description="ATOL protection information text with link to CAA website"
              values={{
                link: (chunks: React.ReactNode) => (
                  <TextLink key="caa-link" weight="regular" href="https://www.caa.co.uk/" target="_blank">
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          </BodyText>
        </VerticalSpacer>}
        <Divider kind="primary" />
        <Group
          direction="vertical"
          gap={32}
          tabletDirection="horizontal"
          tabletVerticalAlign="center"
          tabletHorizontalAlign="space-between"
        >
          {!isStoreMode && <Accreditations
            showProductReviewWinner
          />}
          <BodyText variant="small" align="center" tabletAlign="end">
            <small>{copyrightText} {allRightsReservedText}</small>
          </BodyText>
        </Group>
      </VerticalSpacer>
    </LayoutContainer>
  </Container>
}

function mapStateToProps(state: App.State) {
  return {
    isStoreMode: state.system.storeMode,
    region: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(BottomSection)
